import { useState } from "react";
import { Link } from "react-router";
import { useCart, formatMoney } from "../../lib/cart.tsx";
import { createCheckoutSession, ApiError } from "../../lib/api.ts";

export default function CartDrawer() {
  const {
    isOpen,
    close,
    resolved,
    itemCount,
    subtotalCents,
    currency,
    setQuantity,
    remove,
  } = useCart();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCheckout() {
    if (resolved.length === 0) return;
    setPending(true);
    setError(null);
    try {
      const { url } = await createCheckoutSession(
        resolved.map(({ line }) => ({
          slug: line.slug,
          quantity: line.quantity,
          ...(line.creationSlug ? { creationSlug: line.creationSlug } : {}),
        })),
      );
      window.location.href = url;
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not start checkout — please try again.",
      );
      setPending(false);
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden={!isOpen}
        onClick={close}
        className={[
          "fixed inset-0 z-40 bg-[var(--color-dark)]/40 backdrop-blur-[2px] transition-opacity duration-500",
          isOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0",
        ].join(" ")}
      />

      {/* Drawer */}
      <aside
        aria-label="Your cart"
        aria-hidden={!isOpen}
        className={[
          "fixed z-50 flex flex-col border-[var(--color-rule)] bg-[var(--color-warm-white)] transition-transform duration-500 ease-[cubic-bezier(0.2,0.7,0.2,1)]",
          "bottom-0 right-0 h-[85vh] w-full rounded-t-2xl border-t shadow-[0_-20px_60px_-20px_rgba(15,71,49,0.4)] sm:top-0 sm:h-full sm:max-w-md sm:rounded-none sm:border-t-0 sm:border-l sm:shadow-[-40px_0_80px_-30px_rgba(15,71,49,0.4)]",
          isOpen
            ? "translate-y-0 sm:translate-x-0 sm:translate-y-0"
            : "translate-y-full sm:translate-x-full sm:translate-y-0",
        ].join(" ")}
      >
        <div
          className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-[var(--color-rule)] sm:hidden"
          aria-hidden
          onClick={close}
        />
        <header className="flex items-baseline justify-between border-b border-[var(--color-rule)] px-7 py-5 sm:py-6">
          <div>
            <span className="eyebrow">Your Cart</span>
            <p className="mt-1 font-serif text-sm italic text-[var(--color-stone)]">
              {itemCount === 0
                ? "quiet — nothing chosen yet"
                : `${itemCount} ${itemCount === 1 ? "piece" : "pieces"} in your cart`}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close cart"
            className="text-[var(--color-stone)] transition-colors hover:text-[var(--color-emerald)]"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-7 py-6">
          {resolved.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-6 text-center">
              <p className="font-serif text-xl italic text-[var(--color-stone)]">
                Your cart is quiet.
              </p>
              <Link
                to="/collection"
                onClick={close}
                className="text-[0.7rem] uppercase tracking-[0.2em] text-[var(--color-emerald-deep)] underline decoration-[var(--color-gold)] underline-offset-4 hover:text-[var(--color-emerald)]"
              >
                Visit the collection →
              </Link>
            </div>
          ) : (
            <ul className="flex flex-col gap-6">
              {resolved.map(({ line, product }) => (
                <li
                  key={line.slug}
                  className="flex gap-5 border-b border-[var(--color-rule)]/50 pb-6 last:border-b-0"
                >
                  <Link
                    to={`/product/${product.slug}`}
                    onClick={close}
                    className="block h-24 w-20 shrink-0 overflow-hidden bg-[var(--color-cream)]"
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover"
                    />
                  </Link>
                  <div className="flex flex-1 flex-col">
                    <Link
                      to={`/product/${product.slug}`}
                      onClick={close}
                      className="font-serif text-lg leading-tight text-[var(--color-emerald-deep)] hover:text-[var(--color-emerald)]"
                    >
                      {product.name}
                    </Link>
                    <p className="mt-1 font-serif text-xs italic text-[var(--color-stone)]">
                      {product.scent}
                    </p>
                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex items-center border border-[var(--color-rule)]">
                        <button
                          type="button"
                          onClick={() =>
                            setQuantity(line.slug, line.quantity - 1)
                          }
                          className="flex h-8 w-8 items-center justify-center text-[var(--color-emerald-deep)] transition-colors hover:bg-[var(--color-cream)]"
                          aria-label={`Decrease ${product.name}`}
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-serif text-sm text-[var(--color-emerald-deep)]">
                          {line.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setQuantity(line.slug, line.quantity + 1)
                          }
                          className="flex h-8 w-8 items-center justify-center text-[var(--color-emerald-deep)] transition-colors hover:bg-[var(--color-cream)]"
                          aria-label={`Increase ${product.name}`}
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(line.slug)}
                        className="text-[0.62rem] uppercase tracking-[0.2em] text-[var(--color-stone)] underline decoration-[var(--color-rule)] underline-offset-4 transition-colors hover:text-[var(--color-oxblood)]"
                      >
                        remove
                      </button>
                    </div>
                  </div>
                  <div className="text-right font-serif text-[var(--color-emerald-deep)]">
                    {product.priceCents != null
                      ? formatMoney(
                          product.priceCents * line.quantity,
                          product.currency,
                        )
                      : "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {resolved.length > 0 ? (
          <footer className="border-t border-[var(--color-rule)] bg-[var(--color-cream)]/40 px-7 py-6">
            <div className="flex items-baseline justify-between">
              <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[var(--color-stone)]">
                Subtotal
              </span>
              <span className="font-serif text-2xl text-[var(--color-emerald-deep)]">
                {formatMoney(subtotalCents, currency)}
              </span>
            </div>
            <p className="mt-2 text-[0.65rem] italic text-[var(--color-stone)]">
              Shipping and taxes calculated at checkout.
            </p>

            {error ? (
              <p className="mt-4 text-sm italic text-[var(--color-oxblood)]">
                {error}
              </p>
            ) : null}

            <button
              type="button"
              onClick={handleCheckout}
              disabled={pending}
              className="group relative mt-5 inline-flex w-full items-center justify-center gap-2 overflow-hidden bg-[var(--color-emerald-deep)] px-9 py-4 text-[0.72rem] font-medium uppercase tracking-[0.22em] text-[var(--color-cream)] shadow-[inset_0_0_0_1px_rgba(212,175,55,0.25)] transition-all duration-500 hover:bg-[var(--color-emerald)] hover:shadow-[inset_0_0_0_1px_rgba(212,175,55,0.6),0_8px_30px_-12px_rgba(15,71,49,0.6)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[var(--color-gold)]/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
              />
              <span className="relative">
                {pending ? "Preparing Checkout…" : "Secure Checkout"}
              </span>
            </button>
            <p className="mt-3 text-center text-[0.6rem] uppercase tracking-[0.22em] text-[var(--color-stone)]">
              Secure checkout via Stripe
            </p>
          </footer>
        ) : null}
      </aside>
    </>
  );
}
